import { lentComponent, useContractMutation, useContractQuery, useInvalidateContract } from "@amtana/browser";
import type { LentToken, MutationResult, QueryResult } from "@amtana/browser";
import { notificationTrpcContract } from "@amtana/notification-contract";
import type {
  MarkAllNoticesReadOutput,
  MarkNoticeReadInput,
  Notice,
  NotificationPreference,
  NotificationSettings,
  SetEmailDigestInput,
  SetNotificationChannelInput,
  SetNotificationEnabledInput,
  SetProductUpdatesInput,
  SetQuietHoursInput,
  SetSnoozeInput,
  SetTipsInput,
  UnreadNoticeCount,
  UnsubscribeInput,
  UnsubscribeOutcome,
} from "@amtana/notification-contract";

// Notification settings and in-app notices for every browser module: the
// settings screen, and the shell's bell and notice list.

export function useNotificationSettings(): QueryResult<NotificationSettings> {
  return useContractQuery(notificationTrpcContract, "settingsForUser", { input: {} });
}

function useSettingsWrite<TInput>(
  name: "setEnabled" | "setChannel",
): MutationResult<NotificationSettings, TInput> {
  const mutation = useContractMutation(notificationTrpcContract, name);
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<NotificationSettings, TInput>;
  return {
    ...typed,
    run: (input) => typed.mutateAsync(input).finally(() => void invalidate(notificationTrpcContract)),
  };
}

export const useSetNotificationEnabled = (): MutationResult<
  NotificationSettings,
  SetNotificationEnabledInput
> => useSettingsWrite("setEnabled");
export const useSetNotificationChannel = (): MutationResult<
  NotificationSettings,
  SetNotificationChannelInput
> => useSettingsWrite("setChannel");

/** Polls so the badge catches a notice raised elsewhere without a manual refresh. */
export function useUnreadNoticeCount(): QueryResult<UnreadNoticeCount> {
  return useContractQuery(notificationTrpcContract, "unreadNoticeCount", {
    input: {},
    refetchInterval: 15_000,
  });
}

export function useNotices(limit = 30): QueryResult<readonly Notice[]> {
  return useContractQuery(notificationTrpcContract, "noticesForUser", { input: { limit } });
}

export function useMarkNoticeRead(): MutationResult<Notice, MarkNoticeReadInput> {
  const mutation = useContractMutation(notificationTrpcContract, "markNoticeRead");
  const invalidate = useInvalidateContract();
  return {
    ...mutation,
    run: (input) => mutation.mutateAsync(input).finally(() => void invalidate(notificationTrpcContract)),
  };
}

export function useMarkAllNoticesRead(): MutationResult<MarkAllNoticesReadOutput, Record<string, never>> {
  const mutation = useContractMutation(notificationTrpcContract, "markAllNoticesRead");
  const invalidate = useInvalidateContract();
  return {
    ...mutation,
    run: (input) => mutation.mutateAsync(input).finally(() => void invalidate(notificationTrpcContract)),
  };
}

// Do not disturb and digest (S7.05/S7.06/S7.07): a person's own pause and
// roundup preference, beside their per-type settings above.

export function useNotificationPreference(): QueryResult<NotificationPreference> {
  return useContractQuery(notificationTrpcContract, "preferenceForUser", { input: {} });
}

function usePreferenceWrite<TInput>(
  name: "setSnooze" | "clearSnooze" | "setQuietHours" | "setEmailDigest" | "setProductUpdates" | "setTips",
): MutationResult<NotificationPreference, TInput> {
  const mutation = useContractMutation(notificationTrpcContract, name);
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<NotificationPreference, TInput>;
  return {
    ...typed,
    run: (input) => typed.mutateAsync(input).finally(() => void invalidate(notificationTrpcContract)),
  };
}

export const useSetSnooze = (): MutationResult<NotificationPreference, SetSnoozeInput> =>
  usePreferenceWrite("setSnooze");
export const useClearSnooze = (): MutationResult<NotificationPreference, Record<string, never>> =>
  usePreferenceWrite("clearSnooze");
export const useSetQuietHours = (): MutationResult<NotificationPreference, SetQuietHoursInput> =>
  usePreferenceWrite("setQuietHours");
export const useSetEmailDigest = (): MutationResult<NotificationPreference, SetEmailDigestInput> =>
  usePreferenceWrite("setEmailDigest");
/** Turns product-update email on or off (S6.09); on by default. */
export const useSetProductUpdates = (): MutationResult<NotificationPreference, SetProductUpdatesInput> =>
  usePreferenceWrite("setProductUpdates");
/** Turns tips email on or off (S6.09); on by default. */
export const useSetTips = (): MutationResult<NotificationPreference, SetTipsInput> =>
  usePreferenceWrite("setTips");

/** Spends a mailed unsubscribe link's token, from its confirmation screen's button; needs no session. */
export function useUnsubscribeByToken(): MutationResult<UnsubscribeOutcome, UnsubscribeInput> {
  const mutation = useContractMutation(notificationTrpcContract, "unsubscribeByToken");
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<UnsubscribeOutcome, UnsubscribeInput>;
  return {
    ...typed,
    run: (input) => typed.mutateAsync(input).finally(() => void invalidate(notificationTrpcContract)),
  };
}

/** The shell's bell: its own data and state, lent so platform-shell never reads notification's data itself. */
export const NotificationBellHost: LentToken<Record<string, never>> = lentComponent<Record<string, never>>(
  "notification",
  "NotificationBellHost",
);
